import {trigger, transition, style, animate} from "@angular/animations";
import {ChangeDetectionStrategy, Component, Input} from "@angular/core";
import {ToastService} from "../../services/toast.service";
import {NgClass} from "@angular/common";
import {TranslateModule} from "@ngx-translate/core";
import {Toast} from "src/app/modules/shared/interfaces/Toast.interface";

@Component({
  selector: "app-toast",
  templateUrl: "./toast.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("fadeInOut", [
      transition(":enter", [
        style({opacity: 0}),
        animate("0.05s", style({opacity: 0})),
        animate("0.1s", style({opacity: 1})),
      ]),
      transition(":leave", [animate("0.1s", style({opacity: 0}))]),
    ]),
  ],
  imports: [NgClass, TranslateModule],
})
export class ToastComponent {
  private readonly swipeCloseThreshold = 96;
  private readonly swipeMaxOffset = 220;
  private readonly swipeMinOpacity = 0.45;

  @Input() toast!: Toast;
  @Input() message?: string;
  @Input() classes?: string;
  @Input() translatable? = true;
  @Input() offline = false;
  public swipeOffsetX = 0;
  public swipeTransition = "transform 0.16s ease, opacity 0.16s ease";
  private activePointerId: number | null = null;
  private swipeStartX = 0;
  private swipeStartY = 0;
  private isSwiping = false;
  private isHorizontalSwipe = false;
  private hasDirection = false;

  constructor(private readonly toastService: ToastService) {}

  get additionalClasses(): string[] {
    const arr: string[] = [];
    if (this.offline) {
      arr.push("!max-w-[340px]", "sm:!text-sm");
    }
    return arr;
  }

  closeToast() {
    this.toastService.removeToast(this.toast);
  }

  runAction(): void {
    const action = this.toast.onAction;
    this.toast.onAction = undefined;
    this.closeToast();
    action?.();
  }

  onPointerDown(event: PointerEvent): void {
    if (event.target instanceof Element && event.target.closest("button"))
      return;
    if (event.pointerType === "mouse") {
      return;
    }

    this.activePointerId = event.pointerId;
    this.swipeStartX = event.clientX;
    this.swipeStartY = event.clientY;
    this.isSwiping = true;
    this.isHorizontalSwipe = false;
    this.hasDirection = false;
    this.swipeTransition = "none";
    (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(
      event.pointerId
    );
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.isSwiping || this.activePointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - this.swipeStartX;
    const deltaY = event.clientY - this.swipeStartY;

    if (!this.hasDirection) {
      if (Math.abs(deltaX) < 4 && Math.abs(deltaY) < 4) {
        return;
      }
      this.hasDirection = true;
      this.isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    }

    if (!this.isHorizontalSwipe) {
      return;
    }

    this.swipeOffsetX = Math.max(0, Math.min(this.swipeMaxOffset, deltaX));
    event.preventDefault();
  }

  onPointerUp(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId) {
      return;
    }

    const shouldClose = this.swipeOffsetX >= this.swipeCloseThreshold;
    this.resetSwipeState();
    (event.currentTarget as HTMLElement | null)?.releasePointerCapture?.(
      event.pointerId
    );

    if (shouldClose) {
      this.closeToast();
    }
  }

  onPointerCancel(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId) {
      return;
    }
    this.resetSwipeState();
    (event.currentTarget as HTMLElement | null)?.releasePointerCapture?.(
      event.pointerId
    );
  }

  get toastTransform(): string {
    return `translate3d(${this.swipeOffsetX}px, 0, 0)`;
  }

  get toastOpacity(): number {
    const opacity = 1 - this.swipeOffsetX / this.swipeMaxOffset;
    return Math.max(this.swipeMinOpacity, opacity);
  }

  get durationMs(): number {
    return this.toast?.durationMs ?? 3000;
  }

  get toastType(): "success" | "error" | "warning" | "info" {
    const classes = this.classes ?? "";
    if (classes.includes("success")) return "success";
    if (classes.includes("error")) return "error";
    if (classes.includes("warning")) return "warning";
    return "info";
  }

  get toastTypeClass(): string {
    return `toast--${this.toastType}`;
  }

  get combinedClasses(): string[] {
    return [this.toastTypeClass, ...this.additionalClasses];
  }

  get accentColor(): string {
    switch (this.toastType) {
      case "success":
        return "var(--color-toast-success)";
      case "error":
        return "var(--color-toast-error)";
      case "warning":
        return "var(--color-toast-warning)";
      case "info":
      default:
        return "var(--color-toast-info)";
    }
  }

  get accentColorLight(): string {
    switch (this.toastType) {
      case "success":
        return "var(--color-toast-success-light)";
      case "error":
        return "var(--color-toast-error-light)";
      case "warning":
        return "var(--color-toast-warning-light)";
      case "info":
      default:
        return "var(--color-toast-info-light)";
    }
  }

  get accentGradient(): string {
    return `linear-gradient(90deg, ${this.accentColorLight}, ${this.accentColor})`;
  }

  private resetSwipeState(): void {
    this.isSwiping = false;
    this.hasDirection = false;
    this.isHorizontalSwipe = false;
    this.activePointerId = null;
    this.swipeOffsetX = 0;
    this.swipeTransition = "transform 0.16s ease, opacity 0.16s ease";
  }
}
